import type Graph from 'graphology';
import { FolderTree, GitBranch, X } from 'lucide-react';
import { useState } from 'react';

import { useLeftPanelResize } from '../hooks/use-left-panel-resize.js';
import { cn } from '../lib/cn.js';
import { useUiStore } from '../state/ui-store.js';

import { FileTree } from './file-tree.js';
import { ProcessFlowModal } from './process-flow-modal.js';
import { ProcessesList } from './processes-list.js';

type Tab = 'files' | 'processes';

export interface LeftPanelProps {
  readonly graph: Graph | null;
  readonly selectedNodeId: string | null;
  readonly onNodeSelect: (nodeId: string) => void;
  readonly onZoomTo: (nodeId: string) => void;
}

export function LeftPanel({
  graph,
  selectedNodeId,
  onNodeSelect,
  onZoomTo,
}: LeftPanelProps): JSX.Element {
  const open = useUiStore((s) => s.leftPanelOpen);
  const setOpen = useUiStore((s) => s.setLeftPanelOpen);
  const width = useUiStore((s) => s.leftPanelWidth);
  const setWidth = useUiStore((s) => s.setLeftPanelWidth);
  const [tab, setTab] = useState<Tab>('files');
  const [diagramFor, setDiagramFor] = useState<string | null>(null);

  const { onPointerDown } = useLeftPanelResize({ currentWidth: width, setWidth });

  const handleSelect = (id: string): void => {
    onNodeSelect(id);
    onZoomTo(id);
  };

  return (
    <>
      <aside
        aria-label="File tree panel"
        style={{ width }}
        className={cn(
          'glass-panel scrollbar-thin absolute inset-y-0 left-0 z-[5] flex flex-col border-r border-border-subtle text-text-primary shadow-panel transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <header className="flex items-center justify-between gap-2 border-b border-dashed border-border-subtle px-3 py-2">
          <div className="flex gap-1 rounded-lg border border-border-subtle bg-deep/60 p-0.5">
            <TabButton
              active={tab === 'files'}
              onClick={() => setTab('files')}
              icon={<FolderTree className="h-3.5 w-3.5" />}
              label="Files"
            />
            <TabButton
              active={tab === 'processes'}
              onClick={() => setTab('processes')}
              icon={<GitBranch className="h-3.5 w-3.5" />}
              label="Processes"
            />
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close file panel"
            className="grid h-7 w-7 place-items-center rounded-md text-text-muted transition-colors hover:bg-hover hover:text-text-primary"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex min-h-0 flex-1 flex-col">
          {tab === 'files' ? (
            <FileTree graph={graph} selectedNodeId={selectedNodeId} onNodeSelect={handleSelect} />
          ) : (
            <ProcessesList
              graph={graph}
              onNodeSelect={handleSelect}
              onViewDiagram={setDiagramFor}
            />
          )}
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize file panel"
          onPointerDown={onPointerDown}
          className="absolute inset-y-0 right-0 z-10 w-1.5 -translate-x-0 cursor-col-resize bg-transparent hover:bg-accent/40"
          style={{ touchAction: 'none' }}
        />
      </aside>
      <ProcessFlowModal
        graph={graph}
        processNodeId={diagramFor}
        onClose={() => setDiagramFor(null)}
      />
    </>
  );
}

interface TabButtonProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly icon: React.ReactNode;
  readonly label: string;
}

function TabButton({ active, onClick, icon, label }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-all',
        active
          ? 'bg-accent/15 text-accent shadow-[inset_0_0_0_1px_rgba(124,58,237,0.35)]'
          : 'text-text-secondary hover:bg-hover hover:text-text-primary',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
